import { FunctionComponent, useLayoutEffect, useRef, useState } from 'react';

export interface DiagramPickerValue {
  x: number | string | null | undefined;
  y: number | string | null | undefined;
}

export interface DiagramPickerReference {
  id: string;
  label: string;
  x: number;
  y: number;
}

export interface DiagramPickerProps {
  src?: string | null;
  value: DiagramPickerValue;
  onPick: (coord: { x: number; y: number }) => void;
  maxWidth?: number;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  references?: DiagramPickerReference[];
}

// Reusable image-based coordinate picker. Emits integer pixel coordinates in
// the image's natural resolution so backend-stored (x, y) remain stable even
// when the image is rendered at a different display size.
const DiagramPicker: FunctionComponent<DiagramPickerProps> = ({
  src,
  value,
  onPick,
  maxWidth = 440,
  disabled,
  label,
  helperText,
  references,
}) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [displayed, setDisplayed] = useState<{ w: number; h: number } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [hoverDisp, setHoverDisp] = useState<{ x: number; y: number } | null>(
    null
  );

  const handleLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    setLoadError(false);
  };

  const handleError = () => {
    setNatural(null);
    setDisplayed(null);
    setLoadError(true);
  };

  // Re-measure displayed size on resize so the marker tracks the layout.
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img || !natural) return;
    const ro = new ResizeObserver(() => {
      setDisplayed({ w: img.clientWidth, h: img.clientHeight });
    });
    ro.observe(img);
    return () => ro.disconnect();
  }, [natural]);

  const handleMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (disabled || !natural) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHoverDisp({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const handleLeave = () => setHoverDisp(null);

  const handleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (disabled || !natural) return;
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const naturalX = Math.round((clickX / rect.width) * natural.w);
    const naturalY = Math.round((clickY / rect.height) * natural.h);
    const clamp = (v: number, max: number) => Math.max(0, Math.min(max - 1, v));
    onPick({ x: clamp(naturalX, natural.w), y: clamp(naturalY, natural.h) });
  };

  const numericValue = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const vx = numericValue(value?.x);
  const vy = numericValue(value?.y);

  const markerPx =
    natural && displayed && vx !== null && vy !== null
      ? {
          left: (vx / natural.w) * displayed.w,
          top: (vy / natural.h) * displayed.h,
        }
      : null;

  const referencePins =
    natural && displayed && Array.isArray(references)
      ? references
          .map((r) => {
            const rx = numericValue(r.x);
            const ry = numericValue(r.y);
            if (rx === null || ry === null) return null;
            return {
              id: r.id,
              label: r.label,
              left: (rx / natural.w) * displayed.w,
              top: (ry / natural.h) * displayed.h,
            };
          })
          .filter(
            (p): p is { id: string; label: string; left: number; top: number } =>
              p !== null
          )
      : [];

  if (!src) {
    return (
      <div
        style={{
          border: '1px dashed #cfd3da',
          borderRadius: 4,
          padding: 16,
          textAlign: 'center',
          color: '#6b7280',
          fontSize: 13,
          background: '#fafafa',
        }}>
        {label ? <div style={{ marginBottom: 4, fontWeight: 500 }}>{label}</div> : null}
        No level diagram uploaded yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <div style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{label}</div>
      ) : null}
      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: maxWidth,
          border: '1px solid #e5e7eb',
          borderRadius: 4,
          overflow: 'hidden',
          background: '#f3f4f6',
          cursor: disabled ? 'not-allowed' : 'crosshair',
          opacity: disabled ? 0.6 : 1,
        }}>
        {loadError ? (
          <div style={{ padding: 16, fontSize: 13, color: '#b91c1c' }}>
            Failed to load diagram image.
          </div>
        ) : (
          <img
            ref={imgRef}
            src={src}
            alt="level diagram"
            onLoad={handleLoad}
            onError={handleError}
            onClick={handleClick}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            draggable={false}
            style={{
              display: 'block',
              width: '100%',
              maxWidth: maxWidth,
              height: 'auto',
              userSelect: 'none',
            }}
          />
        )}
        {hoverDisp && displayed && natural && !loadError && !disabled ? (
          <>
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: hoverDisp.y,
                width: '100%',
                height: 1,
                background: '#2563eb',
                opacity: 0.7,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: hoverDisp.x,
                top: 0,
                height: '100%',
                width: 1,
                background: '#2563eb',
                opacity: 0.7,
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: hoverDisp.x + 10,
                top: hoverDisp.y - 20,
                padding: '1px 5px',
                fontSize: 11,
                fontWeight: 700,
                color: '#fff',
                background: '#2563eb',
                borderRadius: 2,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
              }}>
              {`(${Math.round((hoverDisp.x / displayed.w) * natural.w)}, ${Math.round((hoverDisp.y / displayed.h) * natural.h)}) px`}
            </div>
          </>
        ) : null}
        {!loadError
          ? referencePins.map((p) => (
              <div key={p.id} style={{ pointerEvents: 'none' }}>
                <div
                  style={{
                    position: 'absolute',
                    left: p.left - 6,
                    top: p.top - 6,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: '#6b7280',
                    border: '2px solid #fff',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    left: p.left + 8,
                    top: p.top - 16,
                    padding: '0 4px',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#fff',
                    background: '#6b7280',
                    borderRadius: 2,
                    whiteSpace: 'nowrap',
                  }}>
                  {p.label}
                </div>
              </div>
            ))
          : null}
        {markerPx && !loadError ? (
          <>
            <div
              style={{
                position: 'absolute',
                left: markerPx.left - 8,
                top: markerPx.top - 8,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: 'rgba(220, 38, 38, 0.85)',
                border: '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: markerPx.top,
                width: '100%',
                height: 1,
                background: 'rgba(220, 38, 38, 0.35)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: markerPx.left,
                top: 0,
                height: '100%',
                width: 1,
                background: 'rgba(220, 38, 38, 0.35)',
                pointerEvents: 'none',
              }}
            />
          </>
        ) : null}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>
        {natural
          ? `Image: ${natural.w} × ${natural.h} px — click to set X / Y`
          : 'Loading diagram…'}
        {vx !== null && vy !== null ? ` — current: (${vx}, ${vy})` : ''}
      </div>
      {helperText ? (
        <div style={{ fontSize: 12, color: '#b91c1c' }}>{helperText}</div>
      ) : null}
    </div>
  );
};

export default DiagramPicker;
